import { history, type RequestConfig } from '@umijs/max';
import 'flag-icons/css/flag-icons.min.css';
import './global.css';
import {
  errorConfig,
  interceptorConfig,
  loginPath,
  skipPaths,
} from './requestConfig';
import { user as queryCurrentUser } from './services/hub/service';
import { querySites } from './services/site/service';
import { isUnauthorizedRequest } from './utils/requestError';
import { parseSiteId } from './utils/siteUrl';
export type InitialState = {
  fetchUser?: () => Promise<HubAPI.User | undefined>;
  fetchOrganization?: () => Promise<HubAPI.Organization | undefined>;
  user?: HubAPI.User;
  organization?: HubAPI.Organization;
  currentSite?: SiteAPI.SiteListItem;
  settings?: Record<string, unknown>;
  loading?: boolean;
};

export const getInitialState = async (): Promise<InitialState> => {
  const location = history.location;

  const fetchUser = async () => {
    try {
      const response = await queryCurrentUser({ skipErrorHandler: true });
      return response?.data;
    } catch (error: unknown) {
      // This function only calls Hub's current-user endpoint. Umi/Axios may
      // expose a production 401 as error.status without error.config.
      if (isUnauthorizedRequest(error)) {
        const redirect = `${location.pathname}${location.search}${location.hash}`;
        if (!skipPaths.includes(location.pathname)) {
          history.replace({
            pathname: loginPath,
            search: `?redirect=${encodeURIComponent(redirect)}`,
          });
        }
        return undefined;
      }
      throw error;
    }
  };

  const fetchOrganization = async () => {
    try {
      const organization = await querySites(
        { current: 1, pageSize: 100 },
        { skipErrorHandler: true },
      );
      if (!organization.success) return undefined;
      return {
        sites: organization.data,
        total: organization.total,
      };
    } catch (error) {
      console.error(error);
      return undefined;
    }
  };

  if (skipPaths.includes(location.pathname)) {
    return {
      fetchUser,
      fetchOrganization,
      loading: false,
    };
  }

  const currentUser = await fetchUser();
  if (!currentUser) {
    return {
      fetchUser,
      fetchOrganization,
      loading: false,
    };
  }

  const organization = await fetchOrganization();
  const sites = organization?.sites || [];
  const requestedSiteId = parseSiteId(location.search);
  const currentSite =
    sites.find((site) => site.id === requestedSiteId) || sites[0];


  return {
    fetchUser,
    fetchOrganization,
    user: currentUser,
    organization,
    currentSite,
    loading: false,
  };
};

export const request: RequestConfig = {
  ...interceptorConfig,
  ...errorConfig,
  withCredentials: true,
};

export function rootContainer(container: React.ReactNode) {
  return container;
}
